import { useEffect, useState } from "react";
import client from "@/lib/api";
import usePageTitle from "@/hooks/usePageTitle";
import { MEDIA_KIT } from "@/constants/testIds";

export default function MediaKit() {
  const [profile, setProfile] = useState(null);
  usePageTitle("Media Kit", "Press resources for Shri Nitin Narang — approved biographies (short, medium, long), official photograph, pull-quotes, expertise topics and press contacts.");
  useEffect(() => { client.get("/profile").then(r => setProfile(r.data)); }, []);

  if (!profile) return null;

  const bios = [
    { len: "Short (~50 words)", text: profile.short_bio },
    { len: "Medium (~100 words)", text: profile.medium_bio },
    { len: "Long (~250 words)", text: profile.long_bio },
  ];

  const copy = (text) => navigator.clipboard?.writeText(text);

  return (
    <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
      <div className="grid lg:grid-cols-12 gap-12 mb-16">
        <div className="lg:col-span-8">
          <div className="eyebrow mb-4">Media Kit</div>
          <h1 className="serif-title text-5xl lg:text-6xl text-[#0a1628] mb-4">Press resources.</h1>
          <p className="text-[#4a4a4a] text-[17px] leading-relaxed max-w-2xl">
            Approved biographies, designation, official photographs and press-contact details for media use.
          </p>
        </div>
        <div className="lg:col-span-4 border hairline p-7 bg-white">
          <div className="eyebrow mb-3">Press contact</div>
          <ul className="space-y-3 text-[14px]">
            <li className="flex items-center gap-3"><i className="fa-solid fa-envelope text-[#b8975d] w-5"></i><a href="mailto:nitin@theideazfactory.com" className="link-underline">nitin@theideazfactory.com</a></li>
            <li className="flex items-center gap-3"><i className="fa-brands fa-whatsapp text-[#b8975d] w-5"></i><a href="https://wa.me/919988884393" className="link-underline">+91 99888 84393</a></li>
            <li className="flex items-center gap-3"><i className="fa-brands fa-linkedin-in text-[#b8975d] w-5"></i><a href="https://www.linkedin.com/in/nitinn/" className="link-underline">linkedin.com/in/nitinn</a></li>
          </ul>
        </div>
      </div>

      <section className="mb-20">
        <div className="eyebrow mb-3">Official designation</div>
        <div className="border hairline bg-white p-6 lg:p-8 flex items-start justify-between gap-6 flex-wrap">
          <p className="font-display text-2xl text-[#0a1628]">{profile.designation}</p>
          <button onClick={() => copy(profile.designation)} data-testid="mk-copy-designation" className="btn btn-ghost">
            <i className="fa-regular fa-copy"></i> Copy
          </button>
        </div>
      </section>

      <section className="mb-20">
        <div className="eyebrow mb-6">Biographies</div>
        <div className="space-y-6">
          {bios.map((b, i) => (
            <div key={i} className="border hairline bg-white p-7">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="text-[12px] tracking-[0.18em] uppercase text-[#6b6b6b]">{b.len}</div>
                <button onClick={() => copy(b.text)} data-testid={`mk-copy-bio-${i}`} className="btn btn-ghost text-[11px] px-3 py-2">
                  <i className="fa-regular fa-copy"></i> Copy
                </button>
              </div>
              <p className="text-[15px] text-[#2c2c2c] leading-[1.8] whitespace-pre-line">{b.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-20">
        <div className="eyebrow mb-6">Speaker introduction</div>
        <div className="border hairline bg-white p-7">
          <p className="font-display text-xl text-[#0a1628] leading-relaxed italic">"{profile.speaker_intro}"</p>
          <button onClick={() => copy(profile.speaker_intro)} data-testid="mk-copy-speaker" className="btn btn-ghost mt-6">
            <i className="fa-regular fa-copy"></i> Copy
          </button>
        </div>
      </section>

      <section className="mb-20">
        <div className="eyebrow mb-6">Official photograph</div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border hairline bg-white p-6">
            <div className="aspect-[4/5] bg-[#ece7da] overflow-hidden mb-4">
              <img src={profile.hero_image} alt="Nitin Narang official portrait" className="w-full h-full object-cover" />
            </div>
            <a href={profile.hero_image} download target="_blank" rel="noreferrer" className="btn w-full justify-center">
              <i className="fa-solid fa-download"></i> Download Portrait
            </a>
          </div>
          <div className="border hairline bg-[#0a1628] text-[#faf8f3] p-8 lg:p-10">
            <div className="eyebrow text-[#b8975d] mb-4">Media usage note</div>
            <p className="text-[15px] text-[#cfc6b3] leading-relaxed">
              Photographs and biographies on this page may be used by media outlets for coverage of Shri Nitin Narang and the All India Chess Federation, with attribution. For exclusive interviews, custom statements, or additional photography, please write to the press contact.
            </p>
            <div className="mt-8 pt-6 border-t border-[rgba(255,255,255,0.18)] text-[12px] tracking-[0.18em] uppercase text-[#9d9580]">
              All content © Office of Nitin Narang
            </div>
          </div>
        </div>
      </section>

      {/* EXPERTISE TOPICS */}
      {profile.expertise_topics?.length > 0 && (
        <section className="mb-20">
          <div className="eyebrow mb-6">Expertise · Topics for bylines, panels and interviews</div>
          <div className="grid md:grid-cols-2 gap-5">
            {profile.expertise_topics.map((t, i) => (
              <div key={i} data-testid={MEDIA_KIT.topic(i)} className="border hairline bg-white p-6">
                <div className="font-display text-lg text-[#0a1628] mb-2 leading-snug">{t.title}</div>
                <p className="text-[13px] text-[#4a4a4a] leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* PULL QUOTES */}
      {profile.pull_quotes?.length > 0 && (
        <section className="mb-20">
          <div className="eyebrow mb-6">Approved pull-quotes</div>
          <div className="grid md:grid-cols-2 gap-5">
            {profile.pull_quotes.map((q, i) => (
              <figure key={i} data-testid={MEDIA_KIT.quote(i)} className="border hairline bg-white p-7 flex flex-col">
                <i className="fa-solid fa-quote-left text-[#b8975d] text-2xl mb-4"></i>
                <blockquote className="font-display text-[17px] text-[#0a1628] italic leading-snug mb-4 flex-1">
                  {q.quote}
                </blockquote>
                <figcaption className="text-[11px] tracking-[0.18em] uppercase text-[#6b6b6b] border-t hairline pt-3">
                  <div>{q.context}</div>
                  <div className="text-[#b8975d] mt-1">— {q.source}</div>
                </figcaption>
                <button onClick={() => copy(q.quote)} className="btn btn-ghost text-[10px] px-3 py-2 mt-4 self-start">
                  <i className="fa-regular fa-copy"></i> Copy quote
                </button>
              </figure>
            ))}
          </div>
        </section>
      )}

      {/* FEATURED IN */}
      {profile.featured_in?.length > 0 && (
        <section className="mb-20">
          <div className="eyebrow mb-6">As featured in</div>
          <div className="border hairline bg-white p-7 flex flex-wrap gap-x-8 gap-y-3 items-center">
            {profile.featured_in.map((p, i) => (
              <a key={i} href={p.url} target="_blank" rel="noreferrer"
                className="font-display text-[15px] text-[#0a1628] italic hover:text-[#b8975d] transition-colors">
                {p.name}
              </a>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
